import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { baseUrl, openBaseUrl, hostName } from 'app/app.config';
import { Notification, NotificationApiResponse, NotificationsApiResponse } from 'app/layout/common/notifications/notifications.types';
import { FirebaseMessagingService } from 'app/core/services/firebase-messaging.service';
import { DeviceService } from 'app/core/services/device.service';
import { NotificationPopupService } from './notification-popup.service';
import { AuthService } from 'app/core/auth/auth.service';
import { MaterialRequestService } from 'app/modules/material-requests/services/material-request.service';
import { map, Observable, ReplaySubject, switchMap, take, tap, Subject, filter, distinctUntilChanged, catchError, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class NotificationsService implements OnDestroy
{
    private _notifications: ReplaySubject<Notification[] | { data: Notification[] }> = new ReplaySubject<Notification[] | { data: Notification[] }>(1);
    private _destroy$ = new Subject<void>();
    private _currentRoute: string = '';

    /**
     * Constructor
     */
    private _lastSavedToken: string | null = null;

    private _isSavingToken: boolean = false;
    private readonly TOKEN_SAVED_STORAGE_KEY = 'fcm_token_saved_token';


    constructor(
        private _httpClient: HttpClient,
        private _firebaseMessaging: FirebaseMessagingService,
        private _deviceService: DeviceService,
        private _notificationPopupService: NotificationPopupService,
        private _authService: AuthService,
        private _router: Router,
        private _materialRequestService: MaterialRequestService
    )
    {
        // Initialize last saved token from storage, but validate it
        const storedToken = this.getSavedTokenFromStorage();
        this._lastSavedToken = storedToken && storedToken.trim().length > 0 ? storedToken.trim() : null;

        
        // Log initialization for debugging
        if (this._lastSavedToken) {
            console.log('Initialized with saved token from storage:', this._lastSavedToken.substring(0, 20) + '...');
        } else {
            console.log('No saved token found in storage, will save new token when available');
        }
        
        this.initializeFirebaseMessaging();
        this.initializeAuthListener();
        this.initializeRouteListener();
    }

    /**
     * Initialize Firebase Messaging integration
     */
    private initializeFirebaseMessaging(): void
    {
        // Get token (will use stored token if available, or request new one)
        this._firebaseMessaging.getCurrentToken().then(token => {
            if (token) {
                console.log('FCM token obtained:', token);
                // Only save to backend if user is authenticated
                this.checkAndSaveToken(token);
            }
        }).catch(error => {
            console.error('Error getting FCM token:', error);
        });

        // Listen to token changes
        this._firebaseMessaging.token$
            .pipe(
                takeUntil(this._destroy$),
                filter(token => token !== null && token !== undefined && token.trim().length > 0),
                distinctUntilChanged((prev, curr) => prev === curr)
            )
            .subscribe(token => {
                if (token) {
                    console.log('FCM token changed/updated:', token.substring(0, 20) + '...');
                    // Only save to backend if user is authenticated
                    this.checkAndSaveToken(token);
                }
            });

        // Subscribe to foreground messages
        this._firebaseMessaging.subscribeToMessages();

        // Listen for new messages
        this._firebaseMessaging.message$
            .pipe(takeUntil(this._destroy$))
            .subscribe(payload => {
                if (payload) {
                    // Handle push notification (add to list)
                    this.handlePushNotification(payload);
                }
            });
    }

    /**
     * Initialize authentication state listener
     */
    private initializeAuthListener(): void
    {
        this._authService.authState$
            .pipe(
                takeUntil(this._destroy$),
                distinctUntilChanged()
            )
            .subscribe(isAuthenticated => {
                this.handleAuthStateChange(isAuthenticated);
            });

        // Force an initial auth evaluation so BehaviorSubject reflects the real state
        this._authService.check().pipe(take(1)).subscribe();
    }

    private handleAuthStateChange(isAuthenticated: boolean): void
    {
        if (isAuthenticated) {
            // Retry getting token with delays to handle async initialization
            this.getTokenWithRetry(0, 10, 500);
            return;
        }

        console.log('User logged out, clearing saved token');
        this._lastSavedToken = null;
        this.clearSavedTokenStorage();
        // Also clear the old 'notiToken' key for backward compatibility
        try {
            localStorage.removeItem('notiToken');
        } catch (error) {
            console.error('Error clearing notiToken:', error);
        }
    }

    /**
     * Get FCM token with retry logic to handle async initialization
     */
    private getTokenWithRetry(attempt: number, maxAttempts: number, delayMs: number): void
    {
        this._firebaseMessaging.getCurrentToken()
            .then(token => {
                if (token) {
                    // Store in 'notiToken' for backward compatibility with auth.service
                    try {
                        localStorage.setItem('notiToken', token);
                    } catch (error) {
                        console.error('Error storing notiToken:', error);
                    }
                    this.checkAndSaveToken(token);
                } else if (attempt < maxAttempts) {
                    // Retry if token not available yet
                    setTimeout(() => {
                        this.getTokenWithRetry(attempt + 1, maxAttempts, delayMs);
                    }, delayMs);
                } else {
                    console.warn('Could not get FCM token after', maxAttempts, 'attempts');
                }
            })
            .catch(error => {
                console.error('Error getting FCM token, attempt', attempt + 1, error);
                if (attempt < maxAttempts) {
                    setTimeout(() => {
                        this.getTokenWithRetry(attempt + 1, maxAttempts, delayMs);
                    }, delayMs);
                }
            });
    }

    /**
     * Check if user is authenticated and save token if needed
     */
    private checkAndSaveToken(token: string): void
    {
        // Validate token
        if (!token || typeof token !== 'string' || token.trim().length === 0) {
            console.warn('Invalid FCM token provided, skipping save');
            return;
        }

        // Check if user is authenticated
        this._authService.check().pipe(take(1)).subscribe(isAuthenticated => {
            if (!isAuthenticated) {
                console.log('User not authenticated, skipping token save to backend');
                return;
            }

            // Normalize tokens for comparison (trim whitespace)
            const normalizedToken = token.trim();
            const normalizedLastSaved = this._lastSavedToken ? this._lastSavedToken.trim() : null;
            const isSameToken = normalizedLastSaved && normalizedLastSaved === normalizedToken;

            console.log('isSameToken', isSameToken);
            console.log('normalizedToken', normalizedToken);
            console.log('normalizedLastSaved', normalizedLastSaved);

            if (isSameToken) {
                console.log('FCM token unchanged and already saved this session, skipping backend call', {
                    currentToken: normalizedToken.substring(0, 20) + '...'
                });
                return;
            }

            if (this._isSavingToken) {
                console.log('Token save already in progress, skipping duplicate request');
                return;
            }

            this._isSavingToken = true;

            console.log('Saving FCM token to backend', {
                tokenPreview: normalizedToken.substring(0, 20) + '...',
                previousToken: normalizedLastSaved ? normalizedLastSaved.substring(0, 20) + '...' : 'none',
                reason: isSameToken ? 'forced refresh on login' : 'token changed'
            });

            // Save token to backend
            this.saveTokenToBackend(normalizedToken).subscribe({
                next: (response) => {
                    console.log('FCM token saved to backend successfully:', response);
                    this._lastSavedToken = normalizedToken;
                    this.saveTokenToStorage(normalizedToken);
                    // Also store in 'notiToken' for backward compatibility with auth.service
                    try {
                        localStorage.setItem('notiToken', normalizedToken);
                    } catch (error) {
                        console.error('Error storing notiToken:', error);
                    }
                    this._isSavingToken = false;
                },
                error: (error) => {
                    console.error('Error saving FCM token to backend:', error);
                    this._isSavingToken = false;
                }
            });
        });
    }

    /**
     * Persist last saved token locally
     */
    private saveTokenToStorage(token: string): void
    {
        try {
            localStorage.setItem(this.TOKEN_SAVED_STORAGE_KEY, token);
        } catch (error) {
            console.error('Error persisting saved token:', error);
        }
    }

    /**
     * Load saved token from storage
     */
    private getSavedTokenFromStorage(): string | null
    {
        try {
            return localStorage.getItem(this.TOKEN_SAVED_STORAGE_KEY);
        } catch (error) {
            console.error('Error reading saved token from storage:', error);
            return null;
        }
    }


    /**
     * Clear saved token storage
     */
    private clearSavedTokenStorage(): void
    {
        try {
            localStorage.removeItem(this.TOKEN_SAVED_STORAGE_KEY);

        } catch (error) {
            console.error('Error clearing saved token storage:', error);
        }
    }

    /**
     * Initialize route listener to track current route
     */
    private initializeRouteListener(): void
    {
        // Get initial route
        this._currentRoute = this._router.url;
        
        // Listen to route changes
        this._router.events
            .pipe(
                takeUntil(this._destroy$),
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe((event: NavigationEnd) => {
                this._currentRoute = event.urlAfterRedirects;
            });
    }

    /**
     * Check if current route is material-requests
     */
    private isMaterialRequestsRoute(): boolean
    {
        return this._currentRoute.includes('/material-requests');
    }

    /**
     * Reload notifications from backend API
     */
    private reloadNotifications(): void
    {
        this.getAll().subscribe({
            next: (response) => {
                console.log('Notifications reloaded after push notification');
            },
            error: (error) => {
                console.error('Error reloading notifications:', error);
            }
        });
    }

    /**
     * Refresh material requests data
     */
    private refreshMaterialRequests(): void
    {
        try {
            this._materialRequestService.refresh();
            console.log('Material requests data refreshed after push notification');
        } catch (error) {
            console.error('Error refreshing material requests:', error);
        }
    }

    /**
     * Handle incoming push notification
     */
    private handlePushNotification(payload: any): void
    {
        const notification = this._firebaseMessaging.convertToNotification(payload);
        
        // Show popup notification
        this._notificationPopupService.show(notification);
        
        // Get current notifications
        this.notifications$.pipe(take(1)).subscribe(currentResponse => {
            // Handle both array and { data: [] } formats
            let currentNotifications: Notification[] = [];
            
            if (Array.isArray(currentResponse)) {
                currentNotifications = currentResponse;
            } else if (currentResponse && typeof currentResponse === 'object' && 'data' in currentResponse && Array.isArray(currentResponse.data)) {
                currentNotifications = currentResponse.data;
            }
            
            // Add new notification at the beginning
            const updatedNotifications = [notification, ...currentNotifications];
            
            // Maintain the same response format
            if (Array.isArray(currentResponse)) {
                this._notifications.next(updatedNotifications);
            } else if (currentResponse && typeof currentResponse === 'object') {
                this._notifications.next({ ...currentResponse, data: updatedNotifications });
            } else {
                this._notifications.next({ data: updatedNotifications });
            }
        });

        // If current route is material-requests, reload notifications and refresh material requests
        if (this.isMaterialRequestsRoute()) {
            console.log('Material requests route detected, reloading data...');
            // Reload notifications from backend API
            this.reloadNotifications();
            // Refresh material requests data (my-request/paginated API)
            this.refreshMaterialRequests();
        }
    }

    /**
     * Save FCM token to backend
     */
    saveTokenToBackend(token: string): Observable<any>
    {
        const deviceInfo = this._deviceService.getDeviceInfo();
        
        const formData = {
            token: token,
            device_id: deviceInfo.deviceType,
            os_version: deviceInfo.os_version,
            client_name: 'web',
            app_version: '1.0.0'
        };

        const url = `${baseUrl}/apis/bidding/customers/token?url=https://${hostName}`;

        return this._httpClient.post(url, formData);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[] | { data: Notification[] }>
    {
        return this._notifications.asObservable();
    }

    /**
     * Force-save the current FCM token (used right after login)
     */
    forceSaveCurrentToken(): void
    {
        this._firebaseMessaging.getCurrentToken()
            .then(token =>
            {
                if (token)
                {
                    this.checkAndSaveToken(token);
                }
            })
            .catch(error =>
            {
                console.error('Error retrieving FCM token during force save:', error);
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Transform API response to Notification interface
     */
    private transformApiNotification(apiNotification: NotificationApiResponse): Notification
    {
        const link = apiNotification.prch_ordr_id ? `/submitquote/${apiNotification.prch_ordr_id}` : undefined;

        return {
            id: apiNotification.id.toString(),
            title: apiNotification.message_title || undefined,
            description: apiNotification.message,
            time: apiNotification.created_at,
            createdAt: apiNotification.created_at,
            read: apiNotification.read_YN === 'Y',
            lotNo: apiNotification.mr_no,
            prch_ordr_id: apiNotification.prch_ordr_id,
            notification_code: apiNotification.notification_code,
            customer_id: apiNotification.customer_id,
            link,
            useRouter: Boolean(link),
        };
    }

    /**
     * Get all notifications
     */
    getAll(): Observable<any>
    {
        return this._httpClient.get<NotificationsApiResponse>(`${baseUrl}/apis/bidding/mr-notifications`).pipe(
            map((response: NotificationsApiResponse) => {
                // Transform API response to match component expectations
                const transformedData = {
                    data: response.data.map(apiNotification => this.transformApiNotification(apiNotification)),
                    status: response.status,
                    status_description: response.status_description
                };
                return transformedData;
            }),
            tap((transformedResponse) => {
                // Update the notifications subject with transformed data
                this._notifications.next(transformedResponse);
            }),
            catchError((error) => {
                console.error('Error fetching notifications:', error);
                // Return empty data structure on error
                const emptyResponse = {
                    data: [],
                    status: 500,
                    status_description: 'Error fetching notifications'
                };
                this._notifications.next(emptyResponse);
                return of(emptyResponse);
            })
        );
    }

    /**
     * Get sample notifications data
     * TODO: Remove this method when API is ready
     */
    private getSampleNotifications(): any
    {
        const now = new Date();
        
        return {
            data: [
                {
                    id: '1',
                    lotNo: 'LOT-001',
                    messageTitle: 'New Material Request',
                    description: 'A new material request has been created for Project Alpha. Please review and submit your quote.',
                    time: new Date(now.getTime() - 5 * 60000).toISOString(), // 5 minutes ago
                    createdAt: new Date(now.getTime() - 5 * 60000).toISOString(),
                    read: false,
                    link: '/material-requests',
                    useRouter: true,
                },
                {
                    id: '2',
                    lotNo: 'LOT-002',
                    messageTitle: 'Quote Submitted',
                    description: 'Your quote for Purchase Order #PO-12345 has been submitted successfully.',
                    time: new Date(now.getTime() - 15 * 60000).toISOString(), // 15 minutes ago
                    createdAt: new Date(now.getTime() - 15 * 60000).toISOString(),
                    read: false,
                    link: '/purchase-orders',
                    useRouter: true,
                },
                {
                    id: '3',
                    lotNo: 'LOT-003',
                    messageTitle: 'Negotiation Request',
                    description: 'Buyer has requested a negotiation for Product: Cement Grade 53. Please review the details.',
                    time: new Date(now.getTime() - 30 * 60000).toISOString(), // 30 minutes ago
                    createdAt: new Date(now.getTime() - 30 * 60000).toISOString(),
                    read: true,
                    link: '/submitquote/123',
                    useRouter: true,
                },
                {
                    id: '4',
                    lotNo: 'LOT-004',
                    messageTitle: 'Offer Accepted',
                    description: 'Your offer for Material Request #MR-789 has been accepted by the buyer.',
                    time: new Date(now.getTime() - 2 * 3600000).toISOString(), // 2 hours ago
                    createdAt: new Date(now.getTime() - 2 * 3600000).toISOString(),
                    read: true,
                    link: '/material-requests',
                    useRouter: true,
                },
                {
                    id: '5',
                    lotNo: 'LOT-005',
                    messageTitle: 'Delivery Date Reminder',
                    description: 'Reminder: Delivery date for Purchase Order #PO-12345 is approaching. Please ensure materials are ready.',
                    time: new Date(now.getTime() - 5 * 3600000).toISOString(), // 5 hours ago
                    createdAt: new Date(now.getTime() - 5 * 3600000).toISOString(),
                    read: false,
                    link: '/purchase-orders',
                    useRouter: true,
                },
                {
                    id: '6',
                    lotNo: 'LOT-006',
                    messageTitle: 'New Vendor Assignment',
                    description: 'You have been assigned as a vendor for Project Beta. Please review the material requirements.',
                    time: new Date(now.getTime() - 1 * 86400000).toISOString(), // 1 day ago
                    createdAt: new Date(now.getTime() - 1 * 86400000).toISOString(),
                    read: true,
                    link: '/material-requests',
                    useRouter: true,
                },
                {
                    id: '7',
                    lotNo: 'LOT-007',
                    messageTitle: 'Quote Rejected',
                    description: 'Your quote for Material Request #MR-456 has been rejected. You can submit a new quote.',
                    time: new Date(now.getTime() - 2 * 86400000).toISOString(), // 2 days ago
                    createdAt: new Date(now.getTime() - 2 * 86400000).toISOString(),
                    read: true,
                    link: '/material-requests',
                    useRouter: true,
                },
                {
                    id: '8',
                    lotNo: 'LOT-008',
                    messageTitle: 'Payment Received',
                    description: 'Payment of ₹50,000 has been received for Purchase Order #PO-12345.',
                    time: new Date(now.getTime() - 3 * 86400000).toISOString(), // 3 days ago
                    createdAt: new Date(now.getTime() - 3 * 86400000).toISOString(),
                    read: true,
                    link: '/purchase-orders',
                    useRouter: true,
                },
            ]
        };
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(response => {
                const notifications = Array.isArray(response) ? response : ((response && typeof response === 'object' && 'data' in response) ? response.data : []);
                return this._httpClient.post<Notification>('api/common/notifications', {notification}).pipe(
                    map((newNotification) =>
                    {
                        const updatedNotifications = [...notifications, newNotification];
                        // Maintain the same response format
                        if (Array.isArray(response)) {
                            this._notifications.next(updatedNotifications);
                        } else if (response && typeof response === 'object') {
                            this._notifications.next({ ...response, data: updatedNotifications });
                        } else {
                            this._notifications.next({ data: updatedNotifications });
                        }
                        return newNotification;
                    }),
                );
            }),
        );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(response => {
                const notifications = Array.isArray(response) ? response : ((response && typeof response === 'object' && 'data' in response) ? response.data : []);
                return this._httpClient.patch<Notification>('api/common/notifications', {
                    id,
                    notification,
                }).pipe(
                    map((updatedNotification: Notification) =>
                    {
                        // Find the index of the updated notification
                        const index = notifications.findIndex(item => item.id === id);

                        // Update the notification
                        if (index >= 0) {
                            notifications[index] = updatedNotification;
                        }

                        // Maintain the same response format
                        if (Array.isArray(response)) {
                            this._notifications.next(notifications);
                        } else if (response && typeof response === 'object') {
                            this._notifications.next({ ...response, data: notifications });
                        } else {
                            this._notifications.next({ data: notifications });
                        }

                        // Return the updated notification
                        return updatedNotification;
                    }),
                );
            }),
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(response => {
                const notifications = Array.isArray(response) ? response : ((response && typeof response === 'object' && 'data' in response) ? response.data : []);
                return this._httpClient.delete<boolean>('api/common/notifications', {params: {id}}).pipe(
                    map((isDeleted: boolean) =>
                    {
                        // Find the index of the deleted notification
                        const index = notifications.findIndex(item => item.id === id);

                        // Delete the notification
                        if (index >= 0) {
                            notifications.splice(index, 1);
                        }

                        // Maintain the same response format
                        if (Array.isArray(response)) {
                            this._notifications.next(notifications);
                        } else if (response && typeof response === 'object') {
                            this._notifications.next({ ...response, data: notifications });
                        } else {
                            this._notifications.next({ data: notifications });
                        }

                        // Return the deleted status
                        return isDeleted;
                    }),
                );
            }),
        );
    }

    /**
     * Mark a notification as read
     *
     * @param id
     */
    markAsRead(id: string): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(response => {
                const notifications = Array.isArray(response) ? response : ((response && typeof response === 'object' && 'data' in response) ? response.data : []);
                return this._httpClient.put(`${baseUrl}/apis/bidding/mr-notifications/read/${id}`, {}).pipe(
                    map(() =>
                    {
                        const updatedNotifications = notifications.map(notification =>
                            notification.id === id
                                ? { ...notification, read: true }
                                : notification
                        );

                        if (Array.isArray(response)) {
                            this._notifications.next(updatedNotifications);
                        } else if (response && typeof response === 'object') {
                            this._notifications.next({ ...response, data: updatedNotifications });
                        } else {
                            this._notifications.next({ data: updatedNotifications });
                        }

                        return true;
                    })
                );
            })
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(response => {
                const notifications = Array.isArray(response) ? response : ((response && typeof response === 'object' && 'data' in response) ? response.data : []);
                return this._httpClient.get<boolean>('api/common/notifications/mark-all-as-read').pipe(
                    map((isUpdated: boolean) =>
                    {
                        // Go through all notifications and set them as read
                        notifications.forEach((notification, index) =>
                        {
                            notifications[index].read = true;
                        });

                        // Maintain the same response format
                        if (Array.isArray(response)) {
                            this._notifications.next(notifications);
                        } else if (response && typeof response === 'object') {
                            this._notifications.next({ ...response, data: notifications });
                        } else {
                            this._notifications.next({ data: notifications });
                        }

                        // Return the updated status
                        return isUpdated;
                    }),
                );
            }),
        );
    }
}
